import React, { useEffect, useState } from 'react'
import { deleteShopOffer, deleteShopAllOffers, fetchShopOffers } from 'actions/views/shops'
import useAction from 'utils/useAction'
import {
  getCurrentShopId,
  getIsShopOffersLoaded,
  getShopOffers,
  getShopOffersFilters,
  getShopOffersPage,
  getShopOffersPageCount,
  getShopOffersSorter,
  getIsSavingActive,
  getIsShopOffersDataFilteredOrSorted,
  getCurrentShopAttributes,
} from 'domains/shops/selectors'
import { useSelector } from 'react-redux'
import { Button, Spin } from 'antd'
import { Buttons, StyledTable } from '../Shops.styles'
import { getRegularRowClass, getRowKey } from 'helpers/tables'
import getColumns from './shopOffersColumns'
import { openConfirmModal, openShopOffersModal } from 'reducers/modals/actions'
import { FIELD_NAMES } from 'const/forms'
import { PAGE_SIZE } from '../../../const/tables'

const ShopOffersTab = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const currentShopId = useSelector(getCurrentShopId)
  const shopAttributes = useSelector(getCurrentShopAttributes)
  const shopOffers = useSelector(getShopOffers)
  const pageCount = useSelector(getShopOffersPageCount)
  const filters = useSelector(getShopOffersFilters)
  const sorter = useSelector(getShopOffersSorter)
  const page = useSelector(getShopOffersPage)
  const isLoaded = useSelector(getIsShopOffersLoaded)
  const isSavingActive = useSelector(getIsSavingActive)

  const fetchShopOffersFunc = useAction(fetchShopOffers)
  const openShopOffersModalFunc = useAction(openShopOffersModal)
  const deleteShopOfferFunc = useAction(deleteShopOffer)
  const deleteShopAllOffersFunc = useAction(deleteShopAllOffers)
  const openConfirmModalFunc = useAction(openConfirmModal)

  const isDataFilteredOrSorted = useSelector(
    getIsShopOffersDataFilteredOrSorted
  )

  const paginationConfig = {
    position: ['bottomCenter'],
    showQuickJumper: true,
    total: pageCount * PAGE_SIZE,
    pageSize: PAGE_SIZE,
    current: page,
    showSizeChanger: false,
  }

  const handleResetFilters = () => {
    fetchShopOffersFunc({
      filters: {},
      sorter: {},
      page: 1,
    })
  }


  const handleDeleteAllShopOffers = () => {
    const callback = () => deleteShopAllOffersFunc({ id: currentShopId })
    const shopName = shopAttributes?.name || 'shop'
    const text =
      <>
      Are you sure you want to delete ALL shop offers for <br /> <b>{shopName} (ID: {currentShopId})</b>?
      <br />
      <span style={{ color: 'red' }}>This action can't be revoked.</span>
    </>
    openConfirmModalFunc({ callback, text })
  }

  const handleDeleteShopOffer = (shopOffer) => {
    const callback = () => deleteShopOfferFunc(shopOffer[FIELD_NAMES.id])
    const text =
      `Are you sure you want to delete shop offer (ID: ${
        shopOffer[FIELD_NAMES.id]
      })? ` + "This action can't be revoked."
    openConfirmModalFunc({ callback, text })
  }

  const handleDeleteSelectedOffers = () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      return
    }

    const callback = async () => {
      // Delete all selected offers sequentially
      for (const offerId of selectedRowKeys) {
        await deleteShopOfferFunc(offerId)
      }
      // Clear selection after deletion
      setSelectedRowKeys([])
      // Refresh the offers list
      fetchShopOffersFunc()
    }

    const selectedCount = selectedRowKeys.length
    const text = (
      <>
        Are you sure you want to delete <b>{selectedCount}</b> selected shop
        offer{selectedCount > 1 ? 's' : ''}?
        <br />
        <span style={{ color: 'red' }}>This action can't be revoked.</span>
      </>
    )
    openConfirmModalFunc({ callback, text })
  }

  const onTableChange = (pagination, filters, sorter) => {
    if (filters.code && Array.isArray(filters.code)) {
      filters.code = filters.code.join(',')
    }
    fetchShopOffersFunc({
      page: pagination.current,
      filters,
      sorter,
    })
  }

  const handleEditOffer = (id) => {
    openShopOffersModalFunc({ id })
  }

  const handleCreateOffer = () => {
    openShopOffersModalFunc()
  }

  const handleUnselectOffers = () => {
    setSelectedRowKeys([])
  }

  const handleRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys)
  }

  useEffect(() => {
    if (currentShopId) fetchShopOffersFunc()
  }, [currentShopId])

  return (
    <Spin spinning={isSavingActive} size="large">
      <Buttons>
        <Button
          onClick={handleResetFilters}
          disabled={!isLoaded || !isDataFilteredOrSorted}
          style={{ marginLeft: '12px' }}
        >
          Reset filters
        </Button>

        <Button
          onClick={handleCreateOffer}
          disabled={!currentShopId}
          style={{ marginRight: '12px' }}
        >
          Create offer
        </Button>

        <Button
          onClick={handleDeleteSelectedOffers}
          disabled={!currentShopId || !selectedRowKeys || selectedRowKeys.length === 0}
          style={{ marginBottom: '10px' }}
          type="danger"
        >
          Delete Selected ({selectedRowKeys?.length || 0})
        </Button>

        <Button
          onClick={handleUnselectOffers}
          disabled={!currentShopId || !selectedRowKeys || selectedRowKeys.length === 0}
          style={{ marginBottom: '10px',
            marginLeft: '12px',
            backgroundColor:
            currentShopId && selectedRowKeys && selectedRowKeys.length > 0
              ? 'green'
              : undefined,
           }}
        >
          Reset Selection ({selectedRowKeys?.length || 0})
        </Button>

        <Button
          onClick={handleDeleteAllShopOffers}
          disabled={!currentShopId}
          style={{ marginBottom: '10px' }}
        >
          Expire ALL
        </Button>
      </Buttons>

      <StyledTable
        onChange={onTableChange}
        dataSource={shopOffers}
        columns={getColumns(
          filters,
          sorter,
          handleEditOffer,
          handleDeleteShopOffer
        )}
        size="medium"
        pagination={paginationConfig}
        rowKey={getRowKey}
        rowClassName={getRegularRowClass}
        bordered
        sticky={isLoaded}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedRowKeys,
          onChange: handleRowSelectionChange,
          preserveSelectedRowKeys: true,
          hideSelectAll: true,
        }}
      />
    </Spin>
  )
}

export default ShopOffersTab
